import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Modal } from 'react-bootstrap'

import { GET, httpOk } from 'helpers/api'
import { formatDate, formatMoney, regexPhoneNumber } from 'helpers/helpers'

// Окна те же, что в виджете дашборда; бэкенд — /delivery/birthdays?period=
const PERIODS = ['today', 'tomorrow', 'week', 'month']
const PERIOD_COUNT_KEY = { today: 'today', tomorrow: 'tomorrow', week: 'week', month: 'month' }
const ROWS_LIMIT = 300

// Возраст показываем, только если год правдоподобный: кассиры вносят в поле ДР
// дату регистрации, и выходит «исполняется 1 год»
function birthdayAge(age) {
	return age >= 5 && age <= 100 ? age : null
}

/**
 * Дни рождения в колл-центре: пункт в шапке со счётчиком на сегодня и окно со списком.
 * Поздравляют гостей и предлагают заказ обычно операторы, а не владелец — поэтому список
 * здесь, а не только на дашборде. Пункт виден, когда бренд собирает ДР или у гостей
 * ДР уже есть (его вносят кассиры и сами операторы).
 */
function BirthdaysBlock() {
	const { t } = useTranslation()
	const navigate = useNavigate()

	const [summary, setSummary] = useState(null)
	const [show, setShow] = useState(false)
	const [period, setPeriod] = useState('today')
	const [rows, setRows] = useState(null)

	async function load(nextPeriod) {
		setRows(null)
		const response = await GET(`/delivery/birthdays?period=${nextPeriod}`, {}, { loader: false })
		if (!httpOk(response)) return
		setSummary(response.data)
		setRows(response.data?.upcoming ?? [])
	}

	useEffect(() => {
		load('today')
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	function open() {
		setShow(true)
		setPeriod('today')
		load('today')
	}

	function choosePeriod(nextPeriod) {
		setPeriod(nextPeriod)
		load(nextPeriod)
	}

	function createOrder(item) {
		setShow(false)
		navigate(`/delivery/create?phone=${item.phone1}`)
	}

	if (!summary) return null
	const enabled = ['optional', 'required'].includes(summary.birthday_mode)
	if (!enabled && !summary.month) return null

	const total = summary[PERIOD_COUNT_KEY[period]] ?? 0

	return (
		<>
			<div className="delivery-header-item" onClick={open}>
				<div className="position-relative">
					<span><i className="uil uil-gift me-1"></i>{t('birthdays')}</span>
					{summary.today > 0 &&
						<span className="badge bg-danger"
							style={{ 'position': 'absolute', 'right': '-10px', 'top': '-7px' }}>
							{summary.today}
						</span>
					}
				</div>
			</div>

			{/* MODAL BIRTHDAYS */}
			<Modal show={show} animation={false} centered scrollable size="lg" onHide={() => setShow(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('birthdays')}</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ 'height': '520px' }}>
					<div className="d-flex gap-2 mb-3">
						{PERIODS.map(item => (
							<button key={item} type="button"
								className={`btn btn-sm ${period === item ? 'btn-primary' : 'btn-outline-primary'}`}
								onClick={() => choosePeriod(item)}>
								{t(`birthday_${item}`)} <span className="ms-1">{summary[PERIOD_COUNT_KEY[item]] ?? 0}</span>
							</button>
						))}
					</div>

					{rows === null &&
						<div className="text-center text-muted py-5">{t('loading')}</div>
					}
					{rows !== null && rows.length === 0 &&
						<div className="text-center text-muted py-5">{t('birthday_none_period')}</div>
					}
					{rows !== null && rows.length > 0 &&
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th>{t('client')}</th>
									<th className="text-center">{t('birthday')}</th>
									<th className="text-end">{t('cheques')}</th>
									<th className="text-center">{t('last_cheque')}</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								{rows.map(item => {
									const age = birthdayAge(item.age)
									return (
										<tr key={item.id}>
											<td>
												<div>{item.first_name}</div>
												<div className="text-muted" style={{ 'fontSize': '12px' }}>{regexPhoneNumber(item.phone1)}</div>
											</td>
											<td className="text-center">
												<div>{formatDate(item.birthday)}</div>
												<div className="text-muted" style={{ 'fontSize': '12px' }}>
													{item.days_left === 0
														? t('birthday_today')
														: item.days_left === 1
															? t('birthday_tomorrow')
															: t('birthday_in_days', { days: item.days_left })}
													{age && <span> · {t('birthday_turns', { age })}</span>}
												</div>
											</td>
											<td className="text-end">{formatMoney(item.count_cheques ?? 0, 0)}</td>
											<td className="text-center">{formatDate(item.last_cheque_date)}</td>
											<td className="text-center">
												<button type="button" className="btn btn-sm btn-primary text-nowrap"
													onClick={() => createOrder(item)}>
													<i className="uil uil-plus"></i> {t('new_order')}
												</button>
											</td>
										</tr>
									)
								})}
							</tbody>
						</table>
					}
					{rows !== null && total > ROWS_LIMIT &&
						<div className="text-muted mt-2" style={{ 'fontSize': '12px' }}>
							{t('birthday_rows_limited', { shown: ROWS_LIMIT, total })}
						</div>
					}
				</Modal.Body>
			</Modal>
		</>
	)
}

export default BirthdaysBlock
